import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MicrophoneButton } from "@/components/ui/microphone-button";
import { useSpeechToText } from "@/hooks/useSpeechToText";

interface Props {
  loading: boolean;
  onSend: (text: string) => void;
  autoFocus?: boolean;
  compact?: boolean;
}

export function ChatInput({ loading, onSend, autoFocus = false, compact = false }: Props) {
  const [input, setInput] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const { isListening, transcript, startListening, stopListening, resetTranscript } = useSpeechToText();

  useEffect(() => {
    if (transcript) {
      setInput((prev) => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    onSend(input.trim());
    setInput("");
  };

  return (
    <div className="flex gap-2 items-center min-w-0">
      <textarea
        ref={ref}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        placeholder="Pose ta question…"
        rows={1}
        className="flex-1 min-w-0 rounded-md border border-border bg-secondary px-3 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none text-base"
      />
      <MicrophoneButton
        isListening={isListening}
        onStartListening={startListening}
        onStopListening={stopListening}
        size={compact ? "sm" : "default"}
        compact={compact}
      />
      <Button
        size={compact ? "icon" : "default"}
        className={cn("shrink-0", compact ? "h-7 w-7" : "")}
        onClick={handleSend}
        disabled={loading || !input.trim()}
      >
        {loading ? (
          <Loader2 className={`animate-spin ${compact ? "h-3 w-3" : "h-4 w-4"}`} />
        ) : (
          <Send className={compact ? "h-3 w-3" : "h-4 w-4"} />
        )}
      </Button>
    </div>
  );
}
